import React, { ReactNode } from 'react';
import styled from 'styled-components';
import { variant } from 'styled-system';
import { solvSystem, SolvSystemProps, sx, WithSx } from '../../theme/system';
import { Variant } from '../../theme/systemTypes';
import { typography } from '../../theme/typography';
import { style } from '../../utils/css';
import BaseClickable, {
  BaseClickableProps,
} from '../BaseClickable/BaseClickable';

const linkSizes = {
  base: style(typography.body),
  small: style(typography.body2),
};

const linkStyles: Variant<'primary' | 'subtle'> = {
  primary: {
    color: 'crunchberry',
    ':hover': {
      color: 'crunchberry-medium',
    },
    ':disabled, .disabled': {
      color: 'crunchberry-light',
      pointerEvents: 'none',
    },
    ':active': {
      color: 'crunchberry-dark',
    },
    ':visited': {
      color: 'crunchberry',
    },
  },
  subtle: {
    color: 'gray-600',
    ':hover, :active': {
      textDecoration: 'underline',
    },
    ':disabled, .disabled': {
      color: 'gray-300',
      pointerEvents: 'none',
    },
    ':visited': {
      color: 'gray-600',
    },
  },
};

export type LinkProps = SolvSystemProps &
  WithSx &
  BaseClickableProps & {
    children: ReactNode;
    size?: keyof typeof linkSizes;
    subtle?: boolean;
  };

const LinkBase = styled(BaseClickable)<
  LinkProps & { variant?: keyof typeof linkStyles }
>(
  variant({
    prop: 'size',
    variants: linkSizes,
  }),
  variant({
    variants: linkStyles,
  }),
  solvSystem,
  sx,
);

const Link = ({ children, subtle, size = 'base', ...props }: LinkProps) => {
  return (
    <LinkBase
      variant={subtle ? 'subtle' : 'primary'}
      textColor={subtle ? 'gray-600' : 'crunchberry'}
      size={size}
      sx={{
        '--focus-offset': '6px',
      }}
      {...props}
    >
      {children}
    </LinkBase>
  );
};

Link.displayName = 'Link';
export default Link;
